import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { authFetch } from "../utils/auth";
import { RefreshCw, Copy, Check, BarChart3 } from "lucide-react";
import AdminSideBar from "./AdminSideBar";
import UserProfileMenu from "./UserProfileMenu";

// Status-colored favicon now lives in AdminSideBar (rendered on every admin
// page) so it's consistent across the whole admin area, not just this page.

const POLL_INTERVAL = 30000;

function LiveClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const utc = now.toUTCString().split(" ").slice(-2).join(" ").replace("GMT", "UTC");
  const local = now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return (
    <div className="flex items-center gap-2 text-xs font-mono text-gray-400">
      <span>{local} <span className="text-gray-300">{localTz}</span></span>
      <span className="text-gray-200">|</span>
      <span>{utc}</span>
    </div>
  );
}

const FRIENDLY_ERRORS = {
  "timeout": "Request timed out",
  "All connection attempts failed": "Service not reachable",
  "Connection refused": "Service not reachable",
  "no url configured": "Not configured",
  "no host configured": "Not configured",
};

function friendlyError(raw) {
  if (!raw) return null;
  for (const [pattern, msg] of Object.entries(FRIENDLY_ERRORS)) {
    if (raw.includes(pattern)) return { display: msg, raw };
  }
  return { display: raw, raw: null };
}

const SERVICE_PILL = {
  up: { bg: "bg-green-100", text: "text-green-700", dot: "bg-green-500", label: "Up" },
  down: { bg: "bg-red-100", text: "text-red-700", dot: "bg-red-500", label: "Down" },
  degraded: { bg: "bg-orange-100", text: "text-orange-700", dot: "bg-orange-400", label: "Degraded" },
  unknown: { bg: "bg-gray-100", text: "text-gray-500", dot: "bg-gray-300", label: "Unknown" },
};

const STATUS_DOT_COLORS = {
  up: "bg-green-500", down: "bg-red-500", degraded: "bg-orange-400",
  critical: "bg-red-500", unknown: "bg-gray-300",
};

const STATUS_STRIP = {
  up: "bg-green-500", degraded: "bg-orange-400", critical: "bg-red-500",
  down: "bg-red-500", unknown: "bg-gray-200",
};

const HISTORY_BAR_COLORS = {
  up: { bar: "bg-green-400", ring: "hover:ring-green-200" },
  down: { bar: "bg-red-400", ring: "hover:ring-red-200" },
  degraded: { bar: "bg-orange-400", ring: "hover:ring-orange-200" },
  unknown: { bar: "bg-gray-200", ring: "" },
};

const STATUS_BADGE = {
  up: { bg: "bg-green-100", text: "text-green-700", dot: "bg-green-500", label: "Healthy", pulse: true },
  degraded: { bg: "bg-orange-100", text: "text-orange-700", dot: "bg-orange-400", label: "Degraded" },
  critical: { bg: "bg-red-100", text: "text-red-700", dot: "bg-red-500", label: "Critical" },
  unknown: { bg: "bg-gray-100", text: "text-gray-500", dot: "bg-gray-300", label: "Unknown" },
};

function StatusDot({ status, className = "w-2.5 h-2.5" }) {
  return <span className={`${className} rounded-full shrink-0 inline-block ${STATUS_DOT_COLORS[status] || STATUS_DOT_COLORS.unknown}`} />;
}

function ServicePill({ status }) {
  const cfg = SERVICE_PILL[status] || SERVICE_PILL.unknown;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

function StatusBadge({ status }) {
  const cfg = STATUS_BADGE[status] || STATUS_BADGE.unknown;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-0.5 rounded-full ${cfg.bg} ${cfg.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot} ${cfg.pulse ? "animate-pulse" : ""}`} />
      {cfg.label}
    </span>
  );
}

function HistoryDots({ history = [] }) {
  const filled = history.length;
  const empty = 10 - filled;
  return (
    <div className="flex items-end gap-0.5">
      {history.map((entry, i) => {
        const ago = filled - i - 1;
        const colors = HISTORY_BAR_COLORS[entry.status] || HISTORY_BAR_COLORS.unknown;
        const lat = entry.latency ? ` · ${entry.latency}ms` : "";
        const label = (entry.status || "unknown");
        const tip = `${label.charAt(0).toUpperCase() + label.slice(1)} · ${ago === 0 ? "latest" : `${ago} check${ago !== 1 ? "s" : ""} ago`}${lat}`;
        return <span key={i} className={`w-2 h-3.5 rounded-sm ${colors.bar} cursor-help hover:ring-2 ${colors.ring}`} title={tip} />;
      })}
      {Array.from({ length: empty }).map((_, i) => (
        <span key={`e-${i}`} className="w-2 h-3.5 rounded-sm bg-gray-100" title="no data yet" />
      ))}
    </div>
  );
}

function SkeletonLoader() {
  return (
    <div className="animate-pulse">
      <div className="bg-white rounded-xl border border-[#EBECE6] shadow-sm overflow-hidden">
        <div className="px-5 py-3.5 border-b border-[#EBECE6] flex items-center justify-between">
          <div className="h-4 w-28 bg-gray-100 rounded" />
          <div className="h-4 w-20 bg-gray-100 rounded" />
        </div>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="px-5 py-4 border-t border-[#EBECE6] flex items-center gap-4">
            <div className="h-5 w-16 bg-gray-100 rounded-full" />
            <div className="h-4 w-32 bg-gray-100 rounded" />
            <div className="h-4 flex-1 bg-gray-100 rounded" />
            <div className="h-4 w-12 bg-gray-100 rounded" />
          </div>
        ))}
      </div>
      <div className="mt-4 flex gap-4">
        <div className="h-8 w-40 bg-gray-100 rounded-lg" />
        <div className="h-8 w-32 bg-gray-100 rounded-lg" />
        <div className="h-8 w-36 bg-gray-100 rounded-lg" />
      </div>
    </div>
  );
}

function CopyBtn({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    });
  };
  return (
    <button onClick={handleCopy}
      className="opacity-0 group-hover/row:opacity-100 transition shrink-0 text-gray-400 hover:text-cyan-600"
      title="Copy URL">
      {copied ? <Check size={12} className="text-green-500" /> : <Copy size={12} />}
    </button>
  );
}

function getEndpointUrl(svcCfg) {
  if (!svcCfg) return "";
  if (svcCfg.url) return svcCfg.url;
  if (svcCfg.type === "tcp") return "";
  if (svcCfg.host) {
    const scheme = svcCfg.type === "https" ? "https" : "http";
    return `${scheme}://${svcCfg.host}:${svcCfg.port || 80}`;
  }
  return "";
}

function getEndpointDisplay(svcCfg) {
  if (!svcCfg) return "";
  if (svcCfg.url) return svcCfg.url;
  if (svcCfg.host) return `${svcCfg.host}:${svcCfg.port || ""}`;
  return "";
}

const InfraDashboard = ({ onLogout }) => {

  const [environments, setEnvironments] = useState([]);
  const [serviceLabels, setServiceLabels] = useState({});
  const [serviceOrder, setServiceOrder] = useState([]);
  const [activeEnvId, setActiveEnvId] = useState("");
  const [healthData, setHealthData] = useState({});
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState(POLL_INTERVAL / 1000);
  const [checkedAt, setCheckedAt] = useState("");

  const prevStatuses = useRef({});
  const [toasts, setToasts] = useState([]);
  const toastIdRef = useRef(0);
  const serviceLabelsRef = useRef(serviceLabels);
  serviceLabelsRef.current = serviceLabels;

  const addToast = useCallback((title, body, kind) => {
    const id = ++toastIdRef.current;
    setToasts((prev) => [...prev, { id, title, body, kind }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 8000);
  }, []);

  const detectChanges = useCallback(
    (envId, services) => {
      const prev = prevStatuses.current[envId];
      prevStatuses.current[envId] = services;
      if (!prev) return;
      const labels = serviceLabelsRef.current;
      for (const svc of Object.keys(services)) {
        const was = prev[svc];
        const now = services[svc];
        if (was === now) continue;
        const isDown = (s) => s === "down" || s === "degraded";
        const svcLabel = labels[svc] || svc;
        if (!isDown(was) && isDown(now)) {
          addToast(`${svcLabel} ${now === "down" ? "is down" : "is degraded"}`, envId, now);
        } else if (isDown(was) && now === "up") {
          addToast(`${svcLabel} recovered`, envId, "up");
        }
      }
    },
    [addToast]
  );

  const fetchStatus = useCallback(
    async (envId) => {
      try {
        const res = await authFetch(`/api/dashboard/status/${envId}`);
        if (!res.ok) return null;
        const data = await res.json();
        const resolvedId = data.env?.id || envId;
        setHealthData((prev) => ({ ...prev, [resolvedId]: data }));
        setCheckedAt(data.checked_at || "");
        if (data.environments) setEnvironments(data.environments);
        if (data.service_labels) setServiceLabels(data.service_labels);
        if (data.service_order) setServiceOrder(data.service_order);
        const svcStatuses = {};
        for (const [k, v] of Object.entries(data.services || {})) {
          svcStatuses[k] = v.status;
        }
        detectChanges(resolvedId, svcStatuses);
        return data;
      } catch {
        return null;
      }
    },
    [detectChanges]
  );

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      try {
        const data = await fetchStatus("default");
        if (!cancelled && data?.env?.id) {
          setActiveEnvId(data.env.id);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    init();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (environments.length === 0) return;
    const interval = setInterval(() => {
      environments.forEach((e) => fetchStatus(e.id));
      setCountdown(POLL_INTERVAL / 1000);
    }, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [environments, fetchStatus]);

  useEffect(() => {
    const interval = setInterval(() => setCountdown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(interval);
  }, []);

  const switchEnv = (envId) => {
    setActiveEnvId(envId);
    if (!healthData[envId]) fetchStatus(envId);
  };

  const forceRefresh = () => {
    fetchStatus(activeEnvId);
    setCountdown(POLL_INTERVAL / 1000);
  };

  const activeData = healthData[activeEnvId];
  const activeEnv = activeData?.env;
  const activeResult = activeData ? { status: activeData.status, services: activeData.services, backend_meta: activeData.backend_meta } : null;
  const activeHistory = activeData?.history || {};

  const progressPct = ((POLL_INTERVAL / 1000 - countdown) / (POLL_INTERVAL / 1000)) * 100;

  return (
    <div className="flex h-screen w-full bg-[#FAFAF6] text-[#253439] antialiased overflow-hidden">
      <AdminSideBar onLogout={onLogout} />

      {/* Main content */}
      <div className="flex-1 relative flex flex-col px-6 sm:px-10 lg:px-16 xl:px-24 py-8 overflow-auto">
        <div className="absolute top-4 right-6 z-50">
          <UserProfileMenu onLogout={onLogout} />
        </div>
        <div className="w-full">

          {/* Title row */}
          <div className="flex items-start justify-between">
            <div className="flex flex-col gap-1">
              <h1 className="text-2xl font-bold text-[#253439]">Service Status</h1>
              <LiveClock />
            </div>
            <div className="flex items-center gap-2.5">
              <span className="text-xs font-mono text-gray-400 hidden md:inline">refresh in {countdown}s</span>
              <button onClick={forceRefresh}
                className="relative flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-4 py-2 rounded-lg text-sm font-semibold transition overflow-hidden">
                <svg className="w-4 h-4 -rotate-90" viewBox="0 0 20 20">
                  <circle cx="10" cy="10" r="8" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="2" />
                  <circle cx="10" cy="10" r="8" fill="none" stroke="white" strokeWidth="2"
                    strokeDasharray={`${progressPct * 0.5} 50`} strokeLinecap="round" className="transition-all duration-1000" />
                </svg>
                Refresh
              </button>
            </div>
          </div>
          {/* Subtitle — counts + timestamp */}
          {!loading && activeResult && (
            <div className="flex items-center gap-2 mt-1 mb-6 text-sm">
              {(() => {
                const counts = { up: 0, down: 0, degraded: 0 };
                serviceOrder.forEach((svc) => {
                  const s = activeResult?.services?.[svc]?.status;
                  if (s === "up") counts.up++;
                  else if (s === "down") counts.down++;
                  else if (s === "degraded") counts.degraded++;
                });
                return (
                  <>
                    <span className="text-gray-400">{serviceOrder.length} services</span>
                    <span className="text-gray-300">·</span>
                    {counts.down > 0 && <><span className="font-bold text-red-600">{counts.down} down</span><span className="text-gray-300">·</span></>}
                    {counts.degraded > 0 && <><span className="font-bold text-orange-600">{counts.degraded} degraded</span><span className="text-gray-300">·</span></>}
                    {counts.up === serviceOrder.length && <><span className="font-bold text-green-600">All healthy</span><span className="text-gray-300">·</span></>}
                    {checkedAt && <span className="text-gray-400 font-mono text-xs">last checked at {checkedAt}</span>}
                  </>
                );
              })()}
            </div>
          )}
          {loading && <div className="mb-6" />}

          {loading ? (
            <SkeletonLoader />
          ) : environments.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-32 text-center">
              <div className="w-12 h-12 rounded-xl bg-white border border-[#EBECE6] flex items-center justify-center mb-4 shadow-sm">
                <BarChart3 className="w-6 h-6 text-gray-300" />
              </div>
              <p className="text-sm font-semibold text-gray-500">No environments configured</p>
              <p className="text-xs text-gray-400 mt-1 font-mono">
                Add YAML files to <code>server/app/dashboard_config/envs/</code>
              </p>
            </div>
          ) : (
            <>
              {!activeData ? (
                <SkeletonLoader />
              ) : (
                <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
                  {/* Service health table — left 2/3 */}
                  <div className="xl:col-span-2">
                    <div className="bg-white rounded-xl border border-[#EBECE6] shadow-sm overflow-hidden">
                      <div className={`h-1 w-full ${STATUS_STRIP[activeResult?.status] || STATUS_STRIP.unknown}`} />
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-[#f6f6f1] text-left text-gray-600">
                            <th className="px-5 py-3 font-semibold">Service</th>
                            <th className="px-4 py-3 font-semibold w-28">Status</th>
                            <th className="px-4 py-3 font-semibold text-center w-28">History</th>
                            <th className="px-4 py-3 font-semibold text-right w-24">Latency</th>
                            <th className="px-5 py-3 font-semibold text-right w-40">Last OK</th>
                          </tr>
                        </thead>
                        <tbody>
                          {serviceOrder.map((svc) => {
                            const svcRes = activeResult?.services?.[svc] || {};
                            const h = activeHistory[svc] || [];
                            const err = friendlyError(svcRes.error);
                            return (
                              <tr key={svc}
                                className="border-t border-[#EBECE6] hover:bg-[#fafaf8] transition">
                                <td className="px-5 py-3.5">
                                  <div className="min-w-0">
                                    <span className="font-semibold text-[#253439]">{serviceLabels[svc] || svc}</span>
                                    {err && (
                                      <span className="text-xs text-red-500 block truncate" title={err.raw || undefined}>
                                        {err.display}
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-3.5">
                                  <ServicePill status={svcRes.status} />
                                </td>
                                <td className="px-4 py-3.5">
                                  <div className="flex justify-center">
                                    <HistoryDots history={h} />
                                  </div>
                                </td>
                                <td className="px-5 py-3.5 text-right">
                                  {svcRes.latency != null ? (
                                    <span className="font-mono text-xs text-gray-500">{svcRes.latency}ms</span>
                                  ) : (
                                    <span className="font-mono text-xs text-gray-300">—</span>
                                  )}
                                </td>
                                <td className="px-5 py-3.5 text-right text-xs font-mono">
                                  {svcRes.status === "up" ? (
                                    <span className="text-gray-400 tabular-nums" title={activeData?.checked_at_iso}>
                                      {activeData?.checked_at_iso
                                        ? new Date(activeData.checked_at_iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })
                                        : checkedAt}
                                    </span>
                                  ) : h.length > 0 ? (() => {
                                    let ago = 0;
                                    for (let i = h.length - 1; i >= 0; i--) {
                                      if (h[i].status === "up") { ago = h.length - 1 - i; break; }
                                    }
                                    return ago > 0
                                      ? <span className="text-gray-500">{ago} check{ago !== 1 ? "s" : ""} ago</span>
                                      : <span className="text-gray-400">never up</span>;
                                  })() : (
                                    <span className="text-gray-300">—</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Right sidebar — two cards */}
                  <div className="xl:col-span-1 flex flex-col gap-4">

                    {/* Connection card */}
                    <div className="bg-white rounded-xl border border-[#EBECE6] shadow-sm overflow-hidden">
                      <div className="px-5 py-3.5 border-b border-[#EBECE6]">
                        <span className="text-sm font-semibold text-[#253439]">Connection</span>
                      </div>
                      <div className="px-5 py-4 flex flex-col gap-4">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <p className="text-xs font-semibold text-gray-500 mb-1">Environment</p>
                            <p className="text-sm font-semibold text-[#253439]">{activeEnv?.label}</p>
                          </div>
                          {activeEnv?.region && (
                            <div>
                              <p className="text-xs font-semibold text-gray-500 mb-1">Region</p>
                              <p className="text-sm text-[#253439] font-mono">{activeEnv.region}</p>
                            </div>
                          )}
                        </div>

                        <div className="flex flex-col gap-2.5 pt-3 border-t border-[#EBECE6]">
                          <p className="text-xs font-semibold text-gray-500">Endpoints</p>
                          {serviceOrder.map((svc) => {
                            const svcCfg = activeEnv?.services?.[svc] || {};
                            const svcRes = activeResult?.services?.[svc] || {};
                            const epUrl = getEndpointUrl(svcCfg);
                            const epDisplay = getEndpointDisplay(svcCfg);
                            const isTcp = svcCfg.type === "tcp";
                            return (
                              <div key={svc} className="flex flex-col gap-0.5">
                                <div className="flex items-center gap-2">
                                  <StatusDot status={svcRes.status} className="w-2 h-2 shrink-0" />
                                  <p className="text-sm font-semibold text-[#253439]">{serviceLabels[svc] || svc}</p>
                                </div>
                                <div className="pl-4 flex items-center gap-1.5 group/ep">
                                  {epDisplay ? (
                                    <>
                                      {isTcp ? (
                                        <span className="text-xs text-gray-400 font-mono truncate">{epDisplay}</span>
                                      ) : (
                                        <a href={epUrl} target="_blank" rel="noopener noreferrer"
                                          className="text-xs text-gray-400 hover:text-cyan-600 font-mono truncate transition">
                                          {epDisplay}
                                        </a>
                                      )}
                                      <button
                                        onClick={() => navigator.clipboard.writeText(epDisplay)}
                                        className="opacity-0 group-hover/ep:opacity-100 transition shrink-0 text-gray-400 hover:text-[#253439]"
                                        title="Copy">
                                        <Copy size={10} />
                                      </button>
                                    </>
                                  ) : (
                                    <p className="text-xs text-gray-300 font-mono">not configured</p>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Server Config card */}
                    {(() => {
                      const meta = activeResult?.backend_meta || {};
                      return (
                        <div className="bg-white rounded-xl border border-[#EBECE6] shadow-sm overflow-hidden">
                          <div className="px-5 py-3.5 border-b border-[#EBECE6] flex items-center justify-between">
                            <span className="text-sm font-semibold text-[#253439]">Server Config</span>
                            {meta.version && (
                              <span className="text-xs font-mono text-gray-400">v{meta.version}</span>
                            )}
                          </div>
                          <div className="px-5 py-4 flex flex-col gap-0">
                            {meta.model ? (
                              <div className="pb-3 border-b border-[#EBECE6]">
                                <p className="text-xs font-semibold text-gray-500 mb-1.5">Model</p>
                                <span className="inline-block text-xs text-[#253439] font-mono bg-[#FAFAF6] border border-[#EBECE6] px-2.5 py-1 rounded-lg w-full truncate">
                                  {meta.model}
                                </span>
                              </div>
                            ) : null}
                            <div className={`${meta.model ? "pt-3 " : ""}flex flex-col gap-2.5`}>
                              {!meta.model && (
                                <div className="flex items-center justify-between">
                                  <span className="text-sm font-medium text-gray-600">Model</span>
                                  <span className="text-xs text-gray-300 font-mono">—</span>
                                </div>
                              )}
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-gray-600">Semantic Response Cache</span>
                                {meta.semantic_cache_active != null ? (
                                  meta.semantic_cache_active ? (
                                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                                      <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" /> Active
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-orange-700 bg-orange-100 px-2 py-0.5 rounded-full">
                                      <span className="w-1.5 h-1.5 rounded-full bg-orange-400" /> Inactive
                                    </span>
                                  )
                                ) : (
                                  <span className="text-xs text-gray-300 font-mono">—</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Toast notifications */}
      <div className="fixed bottom-5 right-5 z-[70] flex flex-col gap-2 items-end">
        {toasts.map((t) => {
          const colors = t.kind === "up" ? "border-green-200 bg-green-50"
            : t.kind === "degraded" ? "border-orange-200 bg-orange-50" : "border-red-200 bg-red-50";
          const dot = t.kind === "up" ? "bg-green-500" : t.kind === "degraded" ? "bg-orange-400" : "bg-red-500";
          return (
            <div key={t.id} className={`flex items-start gap-2.5 max-w-xs bg-white border shadow-lg rounded-xl px-4 py-3 ${colors}`}>
              <span className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${dot}`} />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[#253439]">{t.title}</p>
                <p className="text-xs text-gray-500 mt-0.5">{t.body}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default InfraDashboard;
